import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

import hospitalAustralImg from "@/assets/brands/hospital-austral.png";
import ls2Img from "@/assets/brands/ls2.png";
import img921 from "@/assets/brands/921.png";
import macImg from "@/assets/brands/mac.png";
import ntoImg from "@/assets/brands/nto.png";
import gudImg from "@/assets/brands/gud.png";
import perlivithaImg from "@/assets/brands/perlivitha.png";
import luminusImg from "@/assets/brands/luminus.png";
import vidabahiaImg from "@/assets/brands/vidabahia.png";
import tucciImg from "@/assets/brands/tucci.png";
import lorenzaImg from "@/assets/brands/lorenza.png";
import arenalesImg from "@/assets/brands/arenales.png";
import dermacycleImg from "@/assets/brands/dermacycle.png";
import ripexImg from "@/assets/brands/ripex.png";
import bangaImg from "@/assets/brands/banga.png";
import seniorgroupImg from "@/assets/brands/seniorgroup.png";
import peltrinaImg from "@/assets/brands/peltrina.png";
import brImg from "@/assets/brands/br.png";
import omicronImg from "@/assets/brands/omicron.png";
import mindhubImg from "@/assets/brands/mindhub.png";
import mundoobdImg from "@/assets/brands/mundoobd.png";
import lusqtoffImg from "@/assets/brands/lusqtoff.png";
import victoriaImg from "@/assets/brands/victoria.png";
import americancottonImg from "@/assets/brands/americancotton.png";
import vitabellaImg from "@/assets/brands/vitabella.png";
import hemImg from "@/assets/brands/hem.png";
import salonbuenoImg from "@/assets/brands/salonbueno.png";
import donquijoteImg from "@/assets/brands/donquijote.png";
import coopincobImg from "@/assets/brands/coopincob.png";
import djImg from "@/assets/brands/dj.png";
import juveniaImg from "@/assets/brands/juvenia.png";
import microsulesImg from "@/assets/brands/microsules.png";

const allBrands = [
  { name: "Hospital Austral", logo: hospitalAustralImg },
  { name: "LS2", logo: ls2Img },
  { name: "921", logo: img921 },
  { name: "MAC", logo: macImg },
  { name: "NTO", logo: ntoImg },
  { name: "güD", logo: gudImg },
  { name: "Perlivitha", logo: perlivithaImg },
  { name: "Luminus", logo: luminusImg },
  { name: "Vida Bahia", logo: vidabahiaImg },
  { name: "Tucci", logo: tucciImg },
  { name: "Lorenza Básicos", logo: lorenzaImg },
  { name: "Arenales Hotel", logo: arenalesImg },
  { name: "MSD Dermacycle", logo: dermacycleImg },
  { name: "Ripex", logo: ripexImg },
  { name: "Banga", logo: bangaImg },
  { name: "The Senior Group", logo: seniorgroupImg },
  { name: "Peltrina", logo: peltrinaImg },
  { name: "BR", logo: brImg },
  { name: "Laboratorio Omicron", logo: omicronImg },
  { name: "Mind Hub", logo: mindhubImg },
  { name: "MundoOBD", logo: mundoobdImg },
  { name: "Lüsqtoff", logo: lusqtoffImg },
  { name: "Victoria Mercería", logo: victoriaImg },
  { name: "American Cotton", logo: americancottonImg },
  { name: "Vita Bella", logo: vitabellaImg },
  { name: "HEM", logo: hemImg },
  { name: "Salón Bueno", logo: salonbuenoImg },
  { name: "Don Quijote", logo: donquijoteImg },
  { name: "Coop INCOB", logo: coopincobImg },
  { name: "DJ", logo: djImg },
  { name: "Juvenia Nutrition", logo: juveniaImg },
  { name: "Microsules Argentina", logo: microsulesImg },
];

const row1 = allBrands.slice(0, 16);
const row2 = allBrands.slice(16, 32);

const MarqueeRow = ({
  brands,
  direction = "left",
  duration = 30,
}: {
  brands: typeof allBrands;
  direction?: "left" | "right";
  duration?: number;
}) => {
  const items = [...brands, ...brands];

  return (
    <div className="relative overflow-hidden w-full">
      <div className="absolute left-0 top-0 bottom-0 w-[25%] md:w-[30%] bg-gradient-to-r from-[#f0f0f0] via-[#f0f0f0]/80 to-transparent z-20 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-[25%] md:w-[30%] bg-gradient-to-l from-[#f0f0f0] via-[#f0f0f0]/80 to-transparent z-20 pointer-events-none" />

      <motion.div
        className="flex gap-4 md:gap-5 w-max mx-auto"
        animate={{
          x: direction === "left" ? ["0%", "-50%"] : ["-50%", "0%"],
        }}
        transition={{ x: { duration, repeat: Infinity, ease: "linear" } }}
      >
        {items.map((brand, i) => (
          <div
            key={`${brand.name}-${i}`}
            className="group relative flex items-center justify-center w-28 h-28 md:w-36 md:h-36 rounded-xl border border-foreground/[0.08] bg-white/60 flex-shrink-0 hover:border-brand-uva/30 hover:bg-white transition-all duration-300"
          >
            <img
              src={brand.logo}
              alt={brand.name}
              width={80}
              height={80}
              className="max-h-[85%] max-w-[85%] object-contain opacity-90 group-hover:opacity-100 transition-all duration-300"
              loading="lazy"
            />
          </div>
        ))}
      </motion.div>
    </div>
  );
};

const BrandsSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { t } = useLanguage();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [130, 0, 0, -60]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.1, 0.9, 1], [0, 1, 1, 0.3]);

  const titleWords = (t("brands.title1") || "").split(" ");

  return (
    <section className="py-24 md:py-32 bg-[#f0f0f0] overflow-hidden" ref={ref}>
      <motion.div className="max-w-6xl mx-auto px-6" style={{ y: contentY, opacity: contentOpacity }}>
        <motion.div className="text-center mb-16 md:mb-20">
          <motion.p
            className="text-xs uppercase tracking-[0.25em] font-bold text-brand-uva mb-4"
            initial={{ opacity: 0, x: -30, filter: "blur(6px)" }}
            animate={inView ? { opacity: 1, x: 0, filter: "blur(0px)" } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {t("brands.tag") || ""}
          </motion.p>
          {/* Word reveal title */}
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black tracking-tight text-foreground lowercase">
            {titleWords.map((word, i) => (
              <span key={i} className="inline-block overflow-hidden mr-[0.25em]">
                <motion.span
                  className="inline-block"
                  initial={{ y: "110%" }}
                  animate={inView ? { y: "0%" } : {}}
                  transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                >
                  {word}
                </motion.span>
              </span>
            ))}{" "}
            <span className="overflow-hidden inline-block">
              <motion.span
                className="inline-block text-brand-uva"
                initial={{ y: "110%" }}
                animate={inView ? { y: "0%" } : {}}
                transition={{ duration: 0.7, delay: titleWords.length * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                {t("brands.highlight")}
              </motion.span>
            </span>
            <br />
            <span className="overflow-hidden inline-block">
              <motion.span
                className="inline-block"
                initial={{ y: "110%" }}
                animate={inView ? { y: "0%" } : {}}
                transition={{ duration: 0.7, delay: (titleWords.length + 1) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                {t("brands.title2")}
              </motion.span>
            </span>
          </h2>
          <motion.p
            className="mt-4 text-foreground/50 text-base md:text-lg max-w-3xl mx-auto"
            initial={{ opacity: 0, filter: "blur(8px)" }}
            animate={inView ? { opacity: 1, filter: "blur(0px)" } : {}}
            transition={{ duration: 0.7, delay: 0.5 }}
          >
            {t("brands.counter")}
          </motion.p>
        </motion.div>
      </motion.div>

      <motion.div
        className="flex flex-col gap-3 md:gap-4 items-center"
        initial={{ opacity: 0, y: 40 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        <MarqueeRow brands={row1} direction="left" duration={30} />
        <MarqueeRow brands={row2} direction="right" duration={35} />
      </motion.div>
    </section>
  );
};

export default BrandsSection;
